import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, apiFetch, isApiError, rawFetch, refreshOnce } from "@/api/client";
import { ep } from "@/api/endpoints";
import { qk } from "@/api/queryKeys";
import { readSse } from "@/api/sse";
import type { Conversation, InsufficientDetails, Message, ModelId, TurnRequest } from "@/api/types";
import { navigateTo } from "@/app/navigation";
import { t } from "@/i18n/ko";
import { useAuthStore } from "@/stores/authStore";
import { useUiStore } from "@/stores/uiStore";
import { TypeBuffer } from "@hamlak/ui/typewriter";
import { currentTyping } from "@/stores/configStore";

export type TurnPhase = "idle" | "sending" | "accepted" | "streaming" | "done" | "failed" | "recovering";

export type TurnError = {
  code: "LLM_FAILED" | "LLM_TIMEOUT" | "CONTENT_FILTERED" | "INTERNAL" | "NETWORK" | "UNKNOWN";
  message: string;
  retryable: boolean;
  refunded?: number;
};

export type TurnState = {
  phase: TurnPhase;
  /** 멱등 키. 재시도는 같은 값. */
  clientTurnId: string | null;
  text: string;
  model: ModelId | null;
  turnId: string | null;
  messageId: string | null;
  /** 화면에 보이는 응답(타이핑 버퍼가 프레임마다 늘린다, AC0-142). 받은 전체는 hook 내부 버퍼. */
  streamed: string;
  error: TurnError | null;
};

const IDLE: TurnState = { phase: "idle", clientTurnId: null, text: "", model: null, turnId: null, messageId: null, streamed: "", error: null };

/** 30초 무이벤트 → 끊김으로 보고 재조회(0.5절). */
const SSE_IDLE_MS = 30_000;
const RECOVER_POLL_MS = 3_000;
const RECOVER_POLL_MAX = 5;

type Options = {
  plotId: string;
  conversationId: string | undefined;
  /** 402 또는 사전 검사 부족. 호출부가 부족 시트를 연다. */
  onInsufficient: (d: InsufficientDetails) => void;
};

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * 1.9 턴 상태기계.
 * idle → sending(낙관 −turnCost, 사용자 말풍선 pending) → accepted(balance 서버값) → streaming(delta 누적) →
 * done(message.done → qk.conversation 캐시 append) | failed(error → 잔액 복원·Callout·재시도 같은 clientTurnId) | recovering(끊김 → 재조회).
 * 스트림 전 JSON 오류: 402 → onInsufficient, 409 → 토스트+재조회, 429 토스트, 400 MODEL_UNAVAILABLE 토스트, 503 → /blocked.
 */
export function useTurnStream({ plotId, conversationId, onInsufficient }: Options) {
  const qc = useQueryClient();
  const [state, setState] = useState<TurnState>(IDLE);
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  const abortRef = useRef<AbortController | null>(null);
  const unmountedRef = useRef(false);
  // 타이핑 버퍼(AC0-142): 받은 조각을 쌓아 두고 프레임마다 몇 글자씩 streamed 에 반영한다(typewriter.ts TypeBuffer).
  const [typing] = useState(
    () =>
      // 언마운트 시 typing.cancel() 로 루프가 멈춘다(그 뒤 setState 는 React 가 무시)
      new TypeBuffer(
        (shown) => {
          setState((s) => (s.streamed === shown ? s : { ...s, phase: s.phase === "accepted" || s.phase === "streaming" ? "streaming" : s.phase, streamed: shown }));
        },
        undefined,
        currentTyping,
      ),
  );
  const onInsufficientRef = useRef(onInsufficient);
  useEffect(() => {
    onInsufficientRef.current = onInsufficient;
  }, [onInsufficient]);

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      abortRef.current?.abort();
      typing.cancel();
    };
  }, [typing]);

  const update = (patch: Partial<TurnState>) => {
    if (unmountedRef.current) return;
    setState((s) => ({ ...s, ...patch }));
  };

  const pushDelta = useCallback((text: string) => typing.push(text), [typing]);
  const finishTyping = useCallback((finalText: string) => typing.finish(finalText), [typing]);
  const revealAll = useCallback(() => typing.revealAll(), [typing]);

  const refetchConversation = useCallback(async (): Promise<Conversation | undefined> => {
    await qc.invalidateQueries({ queryKey: qk.conversation(plotId) });
    const r = await qc.fetchQuery<Conversation>({ queryKey: qk.conversation(plotId), queryFn: () => apiFetch<Conversation>(ep.conversations(plotId)), staleTime: 0 });
    return r;
  }, [qc, plotId]);

  /** 끊김·복귀: 재조회로 마지막 메시지를 확정한다(complete → idle, failed → Callout, pending → 3초 간격 최대 5회). */
  const recover = useCallback(
    async (prevBalance: number | null) => {
      update({ phase: "recovering" });
      for (let i = 0; i < RECOVER_POLL_MAX; i++) {
        let conv: Conversation | undefined;
        try {
          conv = await refetchConversation();
        } catch {
          break;
        }
        if (unmountedRef.current) return;
        const last = conv?.messages.at(-1);
        if (!last || last.role !== "character") break;
        if (last.status === "complete") {
          update({ ...IDLE });
          return;
        }
        if (last.status === "failed") {
          const refunded = typeof prevBalance === "number" && conv ? Math.max(0, conv.balance - prevBalance) : undefined;
          update({ phase: "failed", error: { code: "UNKNOWN", message: t("turn.error.generic"), retryable: true, ...(refunded ? { refunded } : {}) } });
          return;
        }
        await sleep(RECOVER_POLL_MS);
        if (unmountedRef.current) return;
      }
      update({ phase: "failed", error: { code: "UNKNOWN", message: t("turn.error.generic"), retryable: true } });
    },
    [refetchConversation],
  );

  const run = useCallback(
    async (text: string, model: ModelId, clientTurnId: string, before: number | null) => {
      // before = 낙관 차감 전 잔액(send/retry가 넘긴다). 스트림 전 실패·error 이벤트(balance 없음)에서 이 값으로 복원한다.
      if (!conversationId) return;
      const ui = useUiStore.getState();

      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      typing.reset();
      setState({ ...IDLE, phase: "sending", clientTurnId, text, model });

      const body: TurnRequest = { clientTurnId, text, model };
      const restore = () => {
        if (before !== null) useAuthStore.getState().setBalance(before);
      };

      let res: Response;
      try {
        res = await rawFetch(ep.turns(conversationId), { method: "POST", body, signal: ac.signal });
        if (res.status === 401) {
          await refreshOnce();
          res = await rawFetch(ep.turns(conversationId), { method: "POST", body, signal: ac.signal });
        }
      } catch (e) {
        if (ac.signal.aborted) return;
        restore();
        if (isApiError(e) && e.code === "NETWORK") {
          update({ phase: "failed", error: { code: "NETWORK", message: t("error.network"), retryable: true } });
          return;
        }
        update({ phase: "failed", error: { code: "UNKNOWN", message: t("error.generic"), retryable: true } });
        return;
      }

      if (!res.ok) {
        restore();
        let err: ApiError;
        try {
          const envelope = (await res.json()) as { error?: { code?: string; message?: string; details?: unknown } };
          err = new ApiError(res.status, (envelope.error?.code as ApiError["code"]) ?? "UNKNOWN", envelope.error?.message ?? "", envelope.error?.details);
        } catch {
          err = new ApiError(res.status, "UNKNOWN", "");
        }
        if (res.status === 402 && err.code === "INSUFFICIENT_BRICKS") {
          const d = (err.details ?? {}) as Partial<InsufficientDetails>;
          const details: InsufficientDetails = { required: d.required ?? 0, balance: d.balance ?? before ?? 0, model: d.model ?? model };
          useAuthStore.getState().setBalance(details.balance);
          setState({ ...IDLE, clientTurnId, text, model });
          onInsufficientRef.current(details);
          return;
        }
        if (res.status === 409) {
          ui.showSnackbar(err.code === "TURN_IN_PROGRESS" ? t("turn.inProgress") : t("error.generic"));
          setState({ ...IDLE, clientTurnId, text, model });
          void refetchConversation().catch(() => undefined);
          return;
        }
        if (res.status === 503 && err.code === "MAINTENANCE") {
          setState(IDLE);
          navigateTo("/blocked?reason=maintenance", { replace: true });
          return;
        }
        if (res.status === 429) ui.showSnackbar(t("turn.rateLimited"));
        else if (res.status === 400 && err.code === "MODEL_UNAVAILABLE") ui.showSnackbar(t("turn.modelUnavailable"));
        else ui.showSnackbar(t("error.generic"));
        // 서버 message는 i18n 대상이 아니므로 화면에는 공통 문구만.
        update({ phase: "failed", error: { code: "UNKNOWN", message: t("turn.error.generic"), retryable: res.status !== 400 && res.status !== 404 } });
        return;
      }

      // ---- SSE ----
      let idleTimer: ReturnType<typeof setTimeout> | null = null;
      let timedOut = false;
      const armIdle = () => {
        if (idleTimer) clearTimeout(idleTimer);
        idleTimer = setTimeout(() => {
          timedOut = true;
          ac.abort();
        }, SSE_IDLE_MS);
      };
      let finished: "done" | "error" | null = null;
      let acceptedData: { turnId: string; messageId: string; model: ModelId; charged: number } | null = null;
      let fullText = "";
      let commitDone: (() => void) | null = null;

      armIdle();
      let completed = false;
      try {
        const result = await readSse(
          res,
          (ev) => {
            armIdle();
            if (ev.event === "turn.accepted") {
              acceptedData = { turnId: ev.data.turnId, messageId: ev.data.messageId, model: ev.data.model, charged: ev.data.charged };
              useAuthStore.getState().setBalance(ev.data.balance);
              update({ phase: "accepted", turnId: ev.data.turnId, messageId: ev.data.messageId, model: ev.data.model });
            } else if (ev.event === "delta") {
              pushDelta(ev.data.text);
            } else if (ev.event === "message.done") {
              finished = "done";
              fullText = ev.data.content.text;
              useAuthStore.getState().setBalance(ev.data.balance);
              const acc = acceptedData;
              const now = new Date().toISOString();
              const userMsg: Omit<Message, "seq"> = { id: acc?.turnId ?? clientTurnId, role: "user", content: { text }, status: "complete", model: acc?.model ?? model, charged: acc?.charged ?? 0, createdAt: now };
              const charMsg: Omit<Message, "seq"> = { id: acc?.messageId ?? `${clientTurnId}:reply`, role: "character", content: { text: fullText }, status: "complete", model: acc?.model ?? model, createdAt: now };
              const balance = ev.data.balance;
              // 대화 캐시 확정은 타이핑이 다 흐른 뒤(스트림 종료 후 finishTyping → commitDone). 먼저 넣으면 전체 문장이 한 번에 보인다.
              commitDone = () =>
                qc.setQueryData<Conversation>(qk.conversation(plotId), (old) => {
                  if (!old) return old;
                  const rest = old.messages.filter((m) => m.id !== userMsg.id && m.id !== charMsg.id);
                  const base = rest.at(-1)?.seq ?? 0; // 서버 TX1 과 같은 규칙(턴마다 +2)
                  return { ...old, model: acc?.model ?? old.model, balance, messages: [...rest, { ...userMsg, seq: base + 1 }, { ...charMsg, seq: base + 2 }] };
                });
            } else if (ev.event === "error") {
              finished = "error";
              if (typeof ev.data.balance === "number") useAuthStore.getState().setBalance(ev.data.balance);
              else restore();
              const code = ev.data.code;
              const known = code === "LLM_FAILED" || code === "LLM_TIMEOUT" || code === "CONTENT_FILTERED" || code === "INTERNAL";
              update({
                phase: "failed",
                error: {
                  code,
                  message: known ? t(`turn.error.${code}`) : t("turn.error.generic"),
                  retryable: ev.data.retryable,
                  ...(typeof ev.data.refunded === "number" ? { refunded: ev.data.refunded } : {}),
                },
              });
            }
          },
          ac.signal,
        );
        completed = result.completed;
      } catch (e) {
        if (idleTimer) clearTimeout(idleTimer);
        if (unmountedRef.current) return;
        if (ac.signal.aborted && !timedOut) return; // 호출부 abort(복귀 재조회·언마운트)
        // 끊김(AbortError by timeout·네트워크·파싱 실패) → 재조회
        void e;
        revealAll();
        if (finished === null) {
          await recover(before);
          return;
        }
      }
      if (idleTimer) clearTimeout(idleTimer);
      if (finished === "done") {
        await finishTyping(fullText);
        if (unmountedRef.current) return;
        (commitDone as (() => void) | null)?.();
        update({ ...IDLE });
        return;
      }
      revealAll();
      if (finished === "error") return; // failed 상태 유지(Callout·재시도)
      if (!completed) {
        await recover(before);
      }
    },
    [conversationId, plotId, qc, recover, refetchConversation, pushDelta, finishTyping, revealAll, typing],
  );

  /**
   * 전송. 사전 검사(balance < cost → onInsufficient)는 호출부가 turnCost를 알고 있으므로 여기서 한다.
   * 재시도(retry)는 같은 clientTurnId·text·model.
   */
  const send = useCallback(
    (text: string, model: ModelId, cost: number): boolean => {
      const s = stateRef.current;
      if (s.phase === "sending" || s.phase === "accepted" || s.phase === "streaming" || s.phase === "recovering") return false;
      const balance = useAuthStore.getState().balance ?? 0;
      if (balance < cost) {
        onInsufficientRef.current({ required: cost, balance, model });
        // 부족 시트의 "싼 모델로 계속하기"가 같은 글을 재전송할 수 있게 보관한다.
        setState({ ...IDLE, clientTurnId: s.clientTurnId && s.text === text ? s.clientTurnId : crypto.randomUUID(), text, model });
        return false;
      }
      useAuthStore.getState().setBalance(balance - cost);
      const clientTurnId = s.text === text && s.clientTurnId && s.phase !== "done" ? s.clientTurnId : crypto.randomUUID();
      void run(text, model, clientTurnId, balance);
      return true;
    },
    [run],
  );

  /** 실패 Callout의 "다시 시도": 같은 clientTurnId. model을 주면(부족 시트 싼 모델) 그 모델로. */
  const retry = useCallback(
    (cost: number, model?: ModelId): boolean => {
      const s = stateRef.current;
      if (!s.text || !s.clientTurnId) return false;
      const m = model ?? s.model;
      if (!m) return false;
      const balance = useAuthStore.getState().balance ?? 0;
      if (balance < cost) {
        onInsufficientRef.current({ required: cost, balance, model: m });
        return false;
      }
      useAuthStore.getState().setBalance(balance - cost);
      void run(s.text, m, s.clientTurnId, balance);
      return true;
    },
    [run],
  );

  /** 포그라운드 복귀: 진행 중 스트림은 recovering → 재조회. */
  const recoverNow = useCallback(() => {
    const s = stateRef.current;
    if (s.phase === "sending" || s.phase === "accepted" || s.phase === "streaming") {
      abortRef.current?.abort();
      void recover(useAuthStore.getState().balance);
    }
  }, [recover]);

  const dismiss = useCallback(() => setState(IDLE), []);

  const busy = state.phase === "sending" || state.phase === "accepted" || state.phase === "streaming" || state.phase === "recovering";
  return { state, busy, send, retry, recoverNow, dismiss };
}
